import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import { Spinner } from '@/ui';
import { DatePresetBar } from '@/components';
import { useAppDispatch, useAppState } from '@/store';
import { getAdminProfile, selectAdminProfile } from '@/modules/AdminProfile';
import { Resource, usePermissions } from '@/modules/Permissions';
import { DatePresetKey } from '@/models/cancelledOrders.model';
import { TopOrderType } from '@/models/dashboard.model';
import {
  CatalogSection,
  DualLineChart,
  EngagementSection,
  ExportCsvButton,
  MainMetricsKpis,
  PaidOrdersSection,
  RecordsBanner,
  StockSection,
  SuppliersSection,
  TopProductsTable,
  TopSuppliersTable,
} from '../../components';
import { dashboardSelector, getAdminMainMetricsData, setMainMetricsPreset, setMainMetricsQuery } from '../../feature';
import {
  ADMIN_MAIN_METRICS_PRESETS,
  downloadCsv,
  resolveDatePreset,
  resolveGroupByForRange,
  toAdminMainMetricsQuery,
} from '../../helpers';
import { StyledSubtitle } from '../../components/MainMetricsSharedStyles';
import {
  StyledBottomGrid,
  StyledContent,
  StyledEmptyState,
  StyledHeaderText,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledPageWrapper,
} from './DashboardMainContainerStyles';

const DashboardMainContainer = () => {
  const { t } = useTranslation(['dashboard']);
  const dispatch = useAppDispatch();
  const user = useAppState(selectAdminProfile);
  const {
    mainMetricsData: data,
    isMainMetricsLoading: isLoading,
    mainMetricsPreset: preset,
    mainMetricsQuery: query,
  } = useAppState(dashboardSelector);
  const { canView } = usePermissions();
  const canViewDashboard = canView(Resource.DASHBOARD);
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);
  const [suppliersOrderType, setSuppliersOrderType] = useState<TopOrderType>('active');
  const [productsOrderType, setProductsOrderType] = useState<TopOrderType>('active');

  const range = useMemo(() => resolveDatePreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

  useEffect(() => {
    dispatch(getAdminProfile());
  }, [dispatch]);

  useEffect(() => {
    if (!user || !canViewDashboard) return;
    if (preset === 'custom' && (!customFrom || !customTo)) return;

    const groupBy = resolveGroupByForRange(preset, range.dateFrom, range.dateTo);
    const nextQuery = {
      ...toAdminMainMetricsQuery(range, groupBy),
      top_suppliers_order_type: suppliersOrderType,
      top_products_order_type: productsOrderType,
    };
    dispatch(setMainMetricsQuery(nextQuery));
    dispatch(getAdminMainMetricsData(nextQuery));
  }, [user, canViewDashboard, range, preset, customFrom, customTo, suppliersOrderType, productsOrderType, dispatch]);

  const handlePresetChange = (nextPreset: DatePresetKey) => {
    dispatch(setMainMetricsPreset(nextPreset));
    if (nextPreset !== 'custom') {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  const handleCustomRangeChange = (from: Dayjs | null, to: Dayjs | null) => {
    setCustomFrom(from);
    setCustomTo(to);
    dispatch(setMainMetricsPreset('custom'));
  };

  const chartGroupBy = useMemo(
    () => resolveGroupByForRange(preset, range.dateFrom, range.dateTo),
    [preset, range.dateFrom, range.dateTo],
  );

  const handleExportSummary = () => {
    if (!data) return;
    downloadCsv(
      `main-metrics_${query.date_from ?? 'all'}_${query.date_to ?? 'all'}.csv`,
      [t('main_metrics_metric'), t('main_metrics_value'), t('main_metrics_change')],
      [
        [
          t('main_metrics_kpi_active_count'),
          data.kpis.active_orders_count.value,
          data.kpis.active_orders_count.change_percent ?? '',
        ],
        [
          t('main_metrics_kpi_active_amount'),
          data.kpis.active_orders_amount.value,
          data.kpis.active_orders_amount.change_percent ?? '',
        ],
        [t('main_metrics_kpi_avg_check'), data.kpis.avg_check.value, data.kpis.avg_check.change_percent ?? ''],
        [t('main_metrics_kpi_cancelled'), data.kpis.cancelled.count, data.kpis.cancelled.share_percent],
        [t('main_metrics_kpi_new_users'), data.kpis.new_users.value, data.kpis.new_users.change_percent ?? ''],
        [t('main_metrics_kpi_pr'), data.kpis.purchase_rate_pr.value, data.kpis.purchase_rate_pr.change_pp ?? ''],
        [t('main_metrics_kpi_ltv'), data.kpis.customer_ltv.value, data.kpis.customer_ltv.change_percent ?? ''],
        [t('main_metrics_stock_total'), data.stock.total_amount, data.stock.growth_amount],
        [t('main_metrics_suppliers_total'), data.suppliers.total.value, data.suppliers.growth],
      ],
    );
  };

  if (!canViewDashboard) {
    return null;
  }

  return (
    <StyledPageWrapper>
      <StyledHeaderWrapper className="no-print">
        <StyledHeaderText>
          <StyledPageTitle>{t('main_metrics_title')}</StyledPageTitle>
          <StyledSubtitle>{t('main_metrics_subtitle')}</StyledSubtitle>
        </StyledHeaderText>
        <ExportCsvButton disabled={isLoading && !data} onClick={handleExportSummary} />
      </StyledHeaderWrapper>
      <StyledContent>
        {data ? <RecordsBanner records={data.records} /> : null}
        <DatePresetBar
          preset={preset}
          dateFrom={range.dateFrom}
          dateTo={range.dateTo}
          onPresetChange={handlePresetChange}
          onCustomRangeChange={handleCustomRangeChange}
          presets={ADMIN_MAIN_METRICS_PRESETS}
        />
        {isLoading && !data ? <Spinner /> : null}
        {!isLoading && !data ? <StyledEmptyState>{t('main_metrics_empty')}</StyledEmptyState> : null}
        {data ? (
          <>
            <MainMetricsKpis kpis={data.kpis} />
            <DualLineChart
              title={t('main_metrics_active_dynamics')}
              tooltip={t('main_metrics_active_dynamics_tooltip')}
              series={data.active_orders_series}
              initialGroupBy={chartGroupBy}
            />
            <PaidOrdersSection paid={data.paid} groupBy={chartGroupBy} />
            <StockSection stock={data.stock} groupBy={chartGroupBy} />
            <StyledBottomGrid>
              <CatalogSection catalog={data.catalog} />
              <SuppliersSection suppliers={data.suppliers} />
            </StyledBottomGrid>
            <EngagementSection engagement={data.engagement} />
            <TopSuppliersTable
              suppliers={data.top_suppliers}
              orderType={suppliersOrderType}
              onOrderTypeChange={setSuppliersOrderType}
            />
            <TopProductsTable
              products={data.top_products}
              orderType={productsOrderType}
              onOrderTypeChange={setProductsOrderType}
            />
          </>
        ) : null}
      </StyledContent>
    </StyledPageWrapper>
  );
};

export default DashboardMainContainer;
